import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  Divider,
  Chip,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import { Recipe } from '../models/Recipe';
import { getRecipePageNumbers } from '../utils/pageNumbers';

interface RecipeTableOfContentsProps {
  recipes: Recipe[];
  onRecipeSelect: (recipeId: string) => void;
  selectedRecipeId?: string;
  startIndex?: number;
}

const RecipeTableOfContents: React.FC<RecipeTableOfContentsProps> = ({
  recipes,
  onRecipeSelect,
  selectedRecipeId,
  startIndex = 0,
}) => {
  const theme = useTheme();
  
  // Group recipes by category
  const categorizedRecipes = recipes.reduce((acc, recipe) => {
    const category = recipe.analysis?.category.primary || 'other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(recipe);
    return acc;
  }, {} as Record<string, Recipe[]>);
  
  const categoryNames: Record<string, string> = {
    breakfast: 'Breakfast & Brunch',
    lunch: 'Lunch & Light Meals',
    dinner: 'Dinner & Main Courses',
    dessert: 'Desserts & Sweets',
    snack: 'Snacks & Appetizers',
    beverage: 'Beverages',
    sauce: 'Sauces & Condiments',
    soup: 'Soups & Stews',
    salad: 'Salads',
    other: 'Miscellaneous',
  };
  
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };
  
  const item = {
    hidden: { x: -20, opacity: 0 },
    show: { x: 0, opacity: 1 },
  };
  
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        p: 4,
        backgroundColor: theme.palette.mode === 'dark' 
          ? 'rgba(46, 33, 23, 0.95)' 
          : '#FFFEF8',
        backgroundImage: `
          linear-gradient(90deg, transparent 40px, rgba(139, 69, 19, 0.1) 40px, rgba(139, 69, 19, 0.1) 41px, transparent 41px),
          linear-gradient(rgba(139, 69, 19, 0.02) 1px, transparent 1px)
        `,
        backgroundSize: '100% 100%, 100% 30px',
        position: 'relative',
      }}
    >
      {/* Title */}
      <Typography
        variant="h3"
        sx={{
          fontFamily: '"Playfair Display", serif',
          fontWeight: 700,
          color: theme.palette.mode === 'dark' 
            ? 'rgba(255, 248, 240, 0.9)' 
            : '#5D4037',
          textAlign: 'center',
          mb: 1,
          textShadow: theme.palette.mode === 'dark'
            ? '2px 2px 4px rgba(0,0,0,0.3)'
            : '1px 1px 2px rgba(93, 64, 55, 0.2)',
        }}
      >
        Table of Contents
      </Typography>
      
      {/* Decorative Divider */}
      <Box
        sx={{
          width: '200px',
          height: '3px',
          margin: '0 auto',
          mb: 4,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(90deg, transparent, rgba(255, 248, 240, 0.4), transparent)'
            : 'linear-gradient(90deg, transparent, #8B4513, transparent)',
        }}
      />
      
      {/* Recipe List */}
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
      >
        <List sx={{ width: '100%' }}>
          {Object.entries(categorizedRecipes).map(([category, categoryRecipes]) => (
            <React.Fragment key={category}>
              {/* Category Header */}
              <motion.div variants={item}>
                <ListItem sx={{ pb: 1, pt: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: '"Crimson Text", serif',
                      fontWeight: 600,
                      color: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 248, 240, 0.8)' 
                        : '#6D4C41',
                      fontStyle: 'italic',
                      textTransform: 'capitalize',
                      borderBottom: '1px solid',
                      borderColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 248, 240, 0.2)'
                        : 'rgba(139, 69, 19, 0.2)',
                      width: '100%',
                      pb: 1,
                    }}
                  >
                    {categoryNames[category]}
                  </Typography>
                </ListItem>
              </motion.div>
              
              {/* Recipes in Category */}
              {categoryRecipes.map((recipe, index) => (
                <motion.div key={recipe.id} variants={item}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => onRecipeSelect(recipe.id)}
                      selected={selectedRecipeId === recipe.id}
                      sx={{
                        py: 1.5,
                        pl: 4,
                        pr: 2,
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(255, 248, 240, 0.05)'
                            : 'rgba(139, 69, 19, 0.05)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(255, 248, 240, 0.08)'
                            : 'rgba(139, 69, 19, 0.08)',
                          borderLeft: '3px solid',
                          borderColor: 'primary.main',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        {/* Recipe Number */}
                        <Typography
                          sx={{
                            fontFamily: '"Crimson Text", serif',
                            fontSize: '14px',
                            color: theme.palette.mode === 'dark'
                              ? 'rgba(255, 248, 240, 0.4)'
                              : 'rgba(139, 69, 19, 0.5)',
                            minWidth: '30px',
                          }}
                        >
                          {index + 1}.
                        </Typography>
                        
                        {/* Recipe Title */}
                        <Typography
                          sx={{
                            fontFamily: '"Merriweather", serif',
                            fontSize: '16px',
                            color: theme.palette.mode === 'dark'
                              ? 'rgba(255, 248, 240, 0.9)'
                              : '#5D4037',
                            flex: 1,
                            ml: 2,
                          }}
                        >
                          {recipe.title}
                        </Typography>
                        
                        {/* Difficulty Badge */}
                        {recipe.analysis?.difficulty.level && (
                          <Chip
                            label={recipe.analysis.difficulty.level}
                            size="small"
                            sx={{
                              height: '20px',
                              fontSize: '11px',
                              fontFamily: '"Crimson Text", serif',
                              backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(255, 248, 240, 0.1)'
                                : 'rgba(139, 69, 19, 0.1)',
                              color: theme.palette.mode === 'dark'
                                ? 'rgba(255, 248, 240, 0.7)'
                                : '#6D4C41',
                              ml: 2,
                            }}
                          />
                        )}
                        
                        {/* Page Number */}
                        <Typography
                          sx={{
                            fontFamily: '"Crimson Text", serif',
                            fontSize: '14px',
                            color: theme.palette.mode === 'dark'
                              ? 'rgba(255, 248, 240, 0.4)'
                              : 'rgba(139, 69, 19, 0.5)',
                            ml: 2,
                            fontStyle: 'italic',
                          }}
                        >
                          {getRecipePageNumbers(startIndex + recipes.indexOf(recipe)).left}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                  
                  {/* Dotted Line */}
                  <Box
                    sx={{
                      borderBottom: '1px dotted',
                      borderColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 248, 240, 0.1)'
                        : 'rgba(139, 69, 19, 0.1)',
                      mx: 4,
                    }}
                  />
                </motion.div>
              ))}
              
              {/* Category Separator */}
              {category !== Object.keys(categorizedRecipes)[Object.keys(categorizedRecipes).length - 1] && (
                <Divider sx={{ my: 2 }} />
              )}
            </React.Fragment>
          ))}
        </List>
      </motion.div>
      
      {/* Decorative Page Corner */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 20,
          right: 20,
          width: '60px',
          height: '60px',
          borderRight: '2px solid',
          borderBottom: '2px solid',
          borderColor: theme.palette.mode === 'dark'
            ? 'rgba(255, 248, 240, 0.2)'
            : 'rgba(139, 69, 19, 0.2)',
          '&::before': {
            content: '""',
            position: 'absolute',
            bottom: 5,
            right: 5,
            width: '40px',
            height: '40px',
            borderRight: '1px solid',
            borderBottom: '1px solid',
            borderColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 248, 240, 0.1)'
              : 'rgba(139, 69, 19, 0.1)',
          },
        }}
      />
    </Box>
  );
};

export default React.memo(RecipeTableOfContents);
